import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../services/axiosConfig';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Parse JWT token and extract user info
  const parseToken = (token) => {
    try {
      // Check if token is valid
      if (!token || typeof token !== 'string') {
        console.error('Invalid token: token is not a string', { token });
        return null;
      }

      // Check if token has the correct JWT format (3 parts separated by dots)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid token format: expected 3 parts', { parts: parts.length });
        return null;
      }

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const payload = JSON.parse(jsonPayload);
      
      // Check if token is expired
      if (payload.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp < currentTime) {
          console.log('Token expired');
          return null;
        }
      }

      return payload;
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  };

  // Init - Check if user is already logged in via token
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Parse the token to get user info
          const decodedToken = parseToken(token);
          if (decodedToken) {
            // Set the token in axios headers
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            // Map the token payload to our user object
            setUser({
              userId: decodedToken.userId,
              role: decodedToken.role,
              email: decodedToken.email,
              association_id: decodedToken.association_id,
              discipline: decodedToken.discipline,
              territory_id: decodedToken.territory_id,
              region_id: decodedToken.region_id
            });
          } else {
            // Token is invalid or expired, remove it
            console.log('Removing invalid or expired token');
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
          }
        } catch (error) {
          console.error('Authentication error:', error);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      setError(null);
      // Usa sempre il percorso con /api 
      // Send as 'identifier' to support both email and username (cod_affiliata)
      const response = await axios.post('/api/auth/login', { identifier: email, password });
      
      // The backend wraps the response in { success, data: { user, token }, message }
      const token = response.data?.data?.token || response.data?.token;
      
      if (!token) {
        console.error('No token received from server', response.data);
        setError('Login failed: No authentication token received');
        return false;
      }

      // Save token to localStorage
      localStorage.setItem('token', token);

      // Set token in axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Parse token to get user info
      const decodedToken = parseToken(token);
      
      if (!decodedToken) {
        console.error('Failed to parse token');
        setError('Login failed: Invalid token format');
        return false;
      }
      
      // Set user state
      // Using only role now, no need for level
      setUser({
        userId: decodedToken.userId,
        role: decodedToken.role,
        // level field is removed as we're using only role
        email: decodedToken.email,
        association_id: decodedToken.association_id,
        discipline: decodedToken.discipline,
        territory_id: decodedToken.territory_id,
        region_id: decodedToken.region_id
      });      // Redirect based on user role - redirectBasedOnUserRole è definito in Login.js
      // Non facciamo il redirect qui, aspettiamo che lo faccia il componente Login

      return true;
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed. Please check your credentials.');
      return false;
    }
  };
  // Redirect after login based on user role
  const redirectAfterLogin = (role) => {
    const routes = {
      admin: '/admin',
      uisp_national: '/national',
      uisp_regional: '/regional',
      uisp_territorial: '/territorial',
      association: '/association'
    };

    navigate(routes[role] || routes.association);
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    navigate('/login');
  };
  // Check if user has specific role
  const hasRole = (requiredRoles) => {
    if (!user) return false;
    if (user.role === 'admin') return true; // Admin can access everything
    return requiredRoles.includes(user.role);
  };

  // Check if user has committee role with association (dual-role)
  const isDualRole = () => {
    if (!user || !user.association_id) return false;
    const committeeRoles = [
      'uisp_territorial',
      'uisp_regional',
      'uisp_national',
      'uisp_territorial_secretary',
      'uisp_regional_secretary',
      'uisp_nazionale_secretary',
      'sda_territorial',
      'sda_regional',
      'sda_nazionale',
      'cordinator_territorial',
      'cordinator_regional',
      'cordinator_nazionale'
    ];
    return committeeRoles.includes(user.role);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    hasRole,
    isDualRole
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
